import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  patch as patchProfileService,
  getById as getProfileByIdService,
  setActiveProfile,
} from '../services/profileService';
import {
  requestPhoneChangeCode,
  confirmPhoneChange,
} from '../services/authService';
import { useProfileStore } from '../store/useProfileStore';
import type { Profile, OnboardingData } from '../types/profile';
import type { Address } from '../types/address';
import type { NotificationSettings } from '../types/profileSettings';

/**
 * Fetches the active profile from the BE and keeps the store in sync.
 *
 * The active profile is normally hydrated by whoAmI() at app boot / signin
 * and then lives in useProfileStore. That snapshot can go stale — e.g. after
 * a BE-side mutation we didn't initiate from the FE (auto-flips, admin
 * edits, another device updating settings), or simply over time. Pages that
 * surface state the user is likely to scrutinize (Profile → Settings) should
 * use this hook so they always render from a fresh fetch on mount, plus get
 * a queryKey that pull-to-refresh / mutation invalidations can target.
 *
 * `refetchOnMount: 'always'` is what makes this useful — without it, the
 * query reads cached state and never goes back to the BE.
 */
export function useActiveProfile() {
  const activeProfileId = useProfileStore((s) => s.activeProfile?.id);
  return useQuery({
    queryKey: ['profile', activeProfileId],
    queryFn: async () => {
      if (!activeProfileId) throw new Error('no active profile');
      const profile = await getProfileByIdService(activeProfileId);
      // Mirror into the store so non-query consumers (gates, header,
      // anything reading useProfileStore directly) see the fresh value.
      setActiveProfile(profile);
      return profile;
    },
    enabled: !!activeProfileId,
    refetchOnMount: 'always',
  });
}

interface UpdateProfileAddressArgs {
  profileId: string;
  address: Address & { type: 'profile' };
}

/**
 * Patch a profile's `type:'profile'` address and refresh the active profile
 * cache so downstream consumers (e.g. the location gate, Stripe-tax checks)
 * see the new value immediately.
 */
export function useUpdateProfileAddress() {
  return useMutation<Profile, Error, UpdateProfileAddressArgs>({
    mutationFn: async ({ profileId, address }) => {
      await patchProfileService({
        id: profileId,
        address,
      } as Parameters<typeof patchProfileService>[0]);
      const updated = await getProfileByIdService(profileId);
      setActiveProfile(updated);
      return updated;
    },
  });
}

interface UpdateProfileLocationArgs {
  profileId: string;
  postalCode?: string;
  country?: string;
  lon?: number;
  lat?: number;
}

/**
 * Patch only the top-level location scalars (postal/country/lat/lon) without
 * writing a `type:'profile'` entry to `metadata.addresses`. Used by the
 * location gate when we only have a postal-code centroid and not a real
 * street address — that's enough for tax/feed queries but shouldn't masquerade
 * as the user's saved address.
 */
export function useUpdateProfileLocation() {
  return useMutation<Profile, Error, UpdateProfileLocationArgs>({
    mutationFn: async ({ profileId, postalCode, country, lon, lat }) => {
      await patchProfileService({
        id: profileId,
        postalCode,
        country,
        lon,
        lat,
      });
      const updated = await getProfileByIdService(profileId);
      setActiveProfile(updated);
      return updated;
    },
  });
}

interface UpdateProfileDisplayNameArgs {
  profileId: string;
  displayName: string;
}

export function useUpdateProfileDisplayName() {
  return useMutation<Profile, Error, UpdateProfileDisplayNameArgs>({
    mutationFn: async ({ profileId, displayName }) => {
      await patchProfileService({
        id: profileId,
        displayName,
      } as Parameters<typeof patchProfileService>[0]);
      const updated = await getProfileByIdService(profileId);
      setActiveProfile(updated);
      return updated;
    },
  });
}

interface UpdateShareConnectionLevelArgs {
  profileId: string;
  /** 1 = friends & family, 2 = friends of friends, 3 = anyone. */
  shareConnectionLevel: number;
}

/**
 * Persist the chef's default audience reach for the listings they publish.
 * New listings inherit this value at create time; the local-feed SQL filter
 * enforces it against each viewer's connection distance.
 */
export function useUpdateShareConnectionLevel() {
  return useMutation<Profile, Error, UpdateShareConnectionLevelArgs>({
    mutationFn: async ({ profileId, shareConnectionLevel }) => {
      await patchProfileService({
        id: profileId,
        shareConnectionLevel,
      } as Parameters<typeof patchProfileService>[0]);
      const updated = await getProfileByIdService(profileId);
      setActiveProfile(updated);
      return updated;
    },
  });
}

interface UpdateSeeConnectionLevelArgs {
  profileId: string;
  /** 1 = friends & family, 2 = friends of friends, 3 = everyone. */
  seeConnectionLevel: number;
}

/**
 * Persist the user's inbound feed preference — whose listings they want to see
 * in their home feed. The inverse of useUpdateShareConnectionLevel: the
 * local-feed SQL filter folds this in as an extra upper bound on the caller's
 * connection distance to each lister (a listing must clear both its own reach
 * and this preference). The user's own listings are unaffected.
 */
export function useUpdateSeeConnectionLevel() {
  return useMutation<Profile, Error, UpdateSeeConnectionLevelArgs>({
    mutationFn: async ({ profileId, seeConnectionLevel }) => {
      await patchProfileService({
        id: profileId,
        seeConnectionLevel,
      } as Parameters<typeof patchProfileService>[0]);
      const updated = await getProfileByIdService(profileId);
      setActiveProfile(updated);
      return updated;
    },
  });
}

interface UpdateDietaryPreferencesArgs {
  profileId: string;
  /** Dietary tags the dish must carry (`dietary` registry category). */
  dietaryTags: string[];
  /** Allergens the dish must NOT carry (`allergens` registry category). */
  allergenTags: string[];
}

/**
 * Persist the user's own dietary restrictions + avoided allergens. Both arrays
 * are always sent, including empty ones — an empty pair is the "I have no
 * restrictions" answer, and the BE flips `dietaryPrefsSet` to true on any
 * dietary/allergen write, which retires the home-feed prompt card for good.
 * Top-level columns (not metadata), so no read-merge-write dance is needed.
 */
export function useUpdateDietaryPreferences() {
  return useMutation<Profile, Error, UpdateDietaryPreferencesArgs>({
    mutationFn: async ({ profileId, dietaryTags, allergenTags }) => {
      await patchProfileService({
        id: profileId,
        dietaryTags,
        allergenTags,
      } as Parameters<typeof patchProfileService>[0]);
      const updated = await getProfileByIdService(profileId);
      setActiveProfile(updated);
      return updated;
    },
  });
}

interface UpdateProfileLanguageArgs {
  profileId: string;
  language: string;
}

/**
 * Patch the profile's preferred UI / content language (2-char ISO 639-1).
 * BE validates against `config.IsSupportedLanguage` and rejects unknown codes
 * with `error.code: "LANGUAGE_NOT_SUPPORTED"` — apiService translates that
 * to a user-facing message automatically. This hook only handles the BE
 * round-trip; the caller is responsible for firing
 * `i18n.changeLanguage(code)` so the FE dictionary flips. See
 * `src/components/profile/LanguageSwitcher.tsx` for the
 * optimistic-flip-then-PATCH pattern (UI flips first, PATCH races to
 * confirm, revert on failure).
 */
export function useUpdateProfileLanguage() {
  const queryClient = useQueryClient();
  return useMutation<Profile, Error, UpdateProfileLanguageArgs>({
    mutationFn: async ({ profileId, language }) => {
      await patchProfileService({
        id: profileId,
        language,
      } as Parameters<typeof patchProfileService>[0]);
      const updated = await getProfileByIdService(profileId);
      setActiveProfile(updated);
      return updated;
    },
    // Language flip invalidates EVERY cached query — listings, listing
    // details, activities, search, tags, etc. — so background refetches
    // repopulate them in the new lang while React keeps painting the
    // stale-but-already-rendered data. Scoped to the language hook only
    // (other profile patches don't change BE-served content language).
    onSuccess: () => {
      queryClient.invalidateQueries();
    },
  });
}

interface UpdateProfileEmailArgs {
  profileId: string;
  email: string;
}

/**
 * Patch the profile email. Email changes are NOT OTP-verified — matches
 * today's signup parity (email signup also doesn't verify ownership). The
 * backend's Profile.BeforeSave hook rejects cross-user duplicates with 409.
 */
export function useUpdateProfileEmail() {
  return useMutation<Profile, Error, UpdateProfileEmailArgs>({
    mutationFn: async ({ profileId, email }) => {
      await patchProfileService({
        id: profileId,
        email,
      } as Parameters<typeof patchProfileService>[0]);
      const updated = await getProfileByIdService(profileId);
      setActiveProfile(updated);
      return updated;
    },
  });
}

/**
 * Step 1 of the phone add/change flow: request an SMS OTP delivered to the
 * NEW phone number. Backend stores the pending change against the authed
 * user's MetaData; the target phone is read from there at verify time so
 * the client can't swap it between send and confirm.
 */
export function useRequestPhoneChangeCode() {
  return useMutation<void, Error, { phone: string }>({
    mutationFn: async ({ phone }) => {
      await requestPhoneChangeCode(phone);
    },
  });
}

/**
 * Step 2 of the phone add/change flow: verify the OTP. On success the
 * backend commits the new phone to the active profile and returns the
 * updated profile, which we mirror into the store.
 */
export function useConfirmPhoneChange() {
  return useMutation<Profile, Error, { code: string }>({
    mutationFn: async ({ code }) => {
      const updated = await confirmPhoneChange(code);
      setActiveProfile(updated);
      return updated;
    },
  });
}

interface UpdateProfileBioArgs {
  profileId: string;
  bio: string;
}

export function useUpdateProfileBio() {
  return useMutation<Profile, Error, UpdateProfileBioArgs>({
    mutationFn: async ({ profileId, bio }) => {
      await patchProfileService({
        id: profileId,
        bio,
      } as Parameters<typeof patchProfileService>[0]);
      const updated = await getProfileByIdService(profileId);
      setActiveProfile(updated);
      return updated;
    },
  });
}

interface UpdateProfileOnboardingArgs {
  profileId: string;
  onboarding: OnboardingData;
}

export function useUpdateProfileOnboarding() {
  return useMutation<Profile, Error, UpdateProfileOnboardingArgs>({
    mutationFn: async ({ profileId, onboarding }) => {
      const current = await getProfileByIdService(profileId);
      await patchProfileService({
        id: profileId,
        metadata: { ...current.metadata, onboarding },
      } as Parameters<typeof patchProfileService>[0]);
      const updated = await getProfileByIdService(profileId);
      setActiveProfile(updated);
      return updated;
    },
  });
}

interface UpdateNotificationSettingsArgs {
  profileId: string;
  category: 'marketing' | 'notifications';
  settings: NotificationSettings;
}

export function useUpdateNotificationSettings() {
  return useMutation<Profile, Error, UpdateNotificationSettingsArgs>({
    mutationFn: async ({ profileId, category, settings }) => {
      const current = await getProfileByIdService(profileId);
      const updatedSettings = { ...current.metadata.settings };

      if (category === 'marketing') {
        updatedSettings.marketing = settings;
      } else {
        updatedSettings.notifications = { ...updatedSettings.notifications, profile: settings };
      }

      await patchProfileService({
        id: profileId,
        metadata: { ...current.metadata, settings: updatedSettings },
      } as Parameters<typeof patchProfileService>[0]);
      const updated = await getProfileByIdService(profileId);
      setActiveProfile(updated);
      return updated;
    },
  });
}

interface UpdateNewDishSnoozeArgs {
  profileId: string;
  /** true = snooze all new-dish notifications from chefs you know. */
  snoozeNewDish: boolean;
}

/**
 * Persist the global "snooze all new-dish notifications" toggle. Reads the
 * current profile, sets `settings.notifications.snoozeNewDish`, and PATCHes the
 * merged metadata — same deep-merge-client-side pattern as
 * useUpdateNotificationSettings (the BE shallow-merges the top-level `settings`
 * key, so we must send the full settings subtree).
 */
export function useUpdateNewDishSnooze() {
  return useMutation<Profile, Error, UpdateNewDishSnoozeArgs>({
    mutationFn: async ({ profileId, snoozeNewDish }) => {
      const current = await getProfileByIdService(profileId);
      const currentSettings = current.metadata.settings;
      const updatedSettings = {
        ...currentSettings,
        notifications: {
          ...currentSettings.notifications,
          snoozeNewDish,
        },
      };

      await patchProfileService({
        id: profileId,
        metadata: { ...current.metadata, settings: updatedSettings },
      } as Parameters<typeof patchProfileService>[0]);
      const updated = await getProfileByIdService(profileId);
      setActiveProfile(updated);
      return updated;
    },
  });
}
